import { useEffect, useRef, useState } from 'react';
import { ChevronDown, MapPin } from 'lucide-react';
import cn from 'classnames';

import { CardRealtyPhotos, flattenPhotos } from '../CardRealtyPhotos';
import { Dialog } from '../../Dialog';
import { AddRealtyForm } from '../../../form/forms/realtyForm/AddRealtyForm';

import { mainMock } from '../../../../lib/mocks/add-realty-mock/main-mock';
import { equipmentMock } from '../../../../lib/mocks/add-realty-mock/equipment-mock';
import { distancesMock } from '../../../../lib/mocks/add-realty-mock/distances-mock';
import { tarifsMock } from '../../../../lib/mocks/add-realty-mock/tarifs-mock';
import { textDescriptionMock } from '../../../../lib/mocks/add-realty-mock/text-description-mock';
import { realtyStatusMock } from '../../../../data/cardRealty';
import { mockAccordionTitles } from '../../../../data/constants';
import { formatNumberDisplay } from '../../../../utils/utils';

const LABELS = {
  realty_type: 'Type',
  kitchen: 'Kitchens',
  bedroom: 'Bedrooms',
  bathroom: 'Bathrooms',
  apartment_number: 'Apartments',
  meter_type: 'Meter',
  heating_fuel_type: 'Heating fuel',
  heating_owner_type: 'Heating owner',
  parking_parking_owners: 'Parking type',
  parking_garage_owners: 'Garage type',
};

const AREA_FIELDS = ['living_area', 'full_area', 'terrace', 'land_area'];

const MAIN_SECTIONS = [
  {
    title: 'Overview',
    names: [
      'realty_status',
      'urgent_research',
      'observation_types',
      'realty_type',
      'property_name',
      'condition_type',
      'style',
    ],
  },
  { title: 'Areas', names: AREA_FIELDS },
  {
    title: 'Rooms',
    names: [
      'room',
      'living_room',
      'kitchen',
      'bedroom',
      'bathroom',
      'wc',
      'apartment_number',
      'floor',
    ],
  },
  {
    title: 'Features',
    names: [
      'view_type',
      'window_view_type',
      'exposition_type',
      'dpe',
      'ges',
      'hot_water',
      'meter_type',
      'heating_type',
      'heating_fuel_type',
      'heating_owner_type',
      'used_water',
      'ceiling_height',
      'construction_date',
      'renovation_date',
      'vefa',
    ],
  },
  {
    title: 'Parking',
    names: [
      'parking_parking_space',
      'parking_parking_owners',
      'parking_garage_space',
      'parking_garage_owners',
      'parking_storage_space',
    ],
  },
];

// Card triggers pass the editor accordion title they would have opened
const SECTION_BY_TRIGGER = {
  [mockAccordionTitles.Main]: 'Overview',
  [mockAccordionTitles.Tariff]: 'Tariff',
  [mockAccordionTitles.TextDescription]: 'Description',
  [mockAccordionTitles.Videos]: 'Video',
};

const mainFields = Object.fromEntries(
  mainMock.filter(field => field.name).map(field => [field.name, field])
);

const getLabel = field =>
  LABELS[field.name] ??
  field.placeholder ??
  field.checkboxText ??
  field.toggleTitle?.replace(':', '') ??
  field.subtitle ??
  field.name;

const isEmptyTitle = title => !title || title === 'None';

const formatValue = (field, value) => {
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    value === false
  ) {
    return null;
  }

  if (field.fieldType === 'checkbox') return value ? 'Yes' : null;

  if (Array.isArray(value)) {
    const titles = value
      .map(
        item =>
          item?.title ??
          field.checkboxes?.find(
            box => box.value.toString() === item?.toString()
          )?.checkboxText
      )
      .filter(title => !isEmptyTitle(title));

    return titles.length > 0 ? titles.join(', ') : null;
  }

  if (typeof value === 'object') {
    const title = value.title ?? value.name;
    return isEmptyTitle(title) ? null : title;
  }

  const text =
    field.type === 'number' ? formatNumberDisplay(value) : String(value);

  return AREA_FIELDS.includes(field.name) ? `${text} m²` : text;
};

const toRows = (fields, data) =>
  fields
    .filter(field => field.name)
    .map(field => ({
      label: getLabel(field),
      value: formatValue(field, data?.[field.name]),
      isLong: field.fieldType === 'textarea',
    }))
    .filter(row => row.value);

const getInitials = name =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join('');

export const RealtyDetailsDialog = ({ realty = {}, section }) => {
  const {
    id,
    agent,
    location,
    residences,
    mandate,
    main,
    equipment,
    distance,
    tariff,
    text_description,
    youtube_link,
    photo,
  } = realty;
  const photos = photo?.photos;
  const hasPhotos = flattenPhotos(photos).length > 0;

  const focusedSection = SECTION_BY_TRIGGER[section];
  const [openSections, setOpenSections] = useState(
    () => new Set(['Overview', focusedSection].filter(Boolean))
  );
  const sectionRefs = useRef({});

  const street = [
    location?.location_street_number_id?.name,
    location?.location_street_id?.name,
  ]
    .filter(Boolean)
    .join(' ');
  const district = location?.location_district_id?.name;
  const city = location?.location_city_id?.name;
  const address = [street, district, city].filter(Boolean).join(', ');

  const status = realtyStatusMock[Number(main?.realty_status?.value)];
  const price = tariff?.tariff_price ?? tariff?.tariff_netto_price;
  const priceLabel = tariff?.tariff_price ? 'Price' : 'Netto price';

  const tiles = [
    {
      label: 'Type',
      value: formatValue(mainFields.realty_type, main?.realty_type),
    },
    {
      label: 'Living area',
      value: formatValue(mainFields.living_area, main?.living_area),
    },
    {
      label: 'Bedrooms',
      value: formatValue(mainFields.bedroom, main?.bedroom),
    },
    {
      label: 'Bathrooms',
      value: formatValue(mainFields.bathroom, main?.bathroom),
    },
    {
      label: 'Land',
      value: formatValue(mainFields.land_area, main?.land_area),
    },
  ].filter(tile => tile.value);

  const sections = [
    ...MAIN_SECTIONS.map(({ title, names }) => ({
      title,
      rows: toRows(
        names.map(name => mainFields[name]),
        main
      ),
    })),
    {
      title: 'Location',
      rows: [
        { label: 'Address', value: street },
        { label: 'District', value: district },
        { label: 'City', value: city },
        { label: 'Cadastre', value: location?.location_cadastre },
        { label: 'Residence', value: residences?.name },
      ].filter(row => row.value),
    },
    { title: 'Tariff', rows: toRows(tarifsMock, tariff) },
    {
      title: 'Mandates',
      rows: (mandate ?? []).map(item => ({
        label: `${item.mandate_type?.title ?? 'Mandate'}${item.mandate_number ? ` #${item.mandate_number}` : ''}`,
        value: [
          item.mandate_status?.title,
          [item.mandate_start_date, item.mandate_end_date]
            .filter(Boolean)
            .join(' – '),
        ]
          .filter(Boolean)
          .join(', '),
      })),
    },
    { title: 'Equipment', rows: toRows(equipmentMock, equipment) },
    { title: 'Distances', rows: toRows(distancesMock, distance) },
    {
      title: 'Description',
      rows: toRows(textDescriptionMock, text_description),
    },
    {
      title: 'Video',
      rows: youtube_link
        ? [
            {
              label: 'YouTube',
              value: (
                <a
                  href={youtube_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#5b63a8] underline break-all hover:text-[#3e4590]"
                >
                  {youtube_link}
                </a>
              ),
            },
          ]
        : [],
    },
  ].filter(item => item.rows.length > 0);

  const isAllOpen = sections.every(({ title }) => openSections.has(title));

  const toggleSection = title =>
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });

  const toggleAll = () =>
    setOpenSections(
      isAllOpen ? new Set() : new Set(sections.map(({ title }) => title))
    );

  useEffect(() => {
    sectionRefs.current[focusedSection]?.scrollIntoView({ block: 'start' });
  }, [focusedSection]);

  return (
    // Mobile scrolls as a whole; desktop keeps the top block fixed and scrolls the body
    <div className="flex flex-col w-full max-h-[calc(100vh-12rem)] overflow-y-auto hide-scroll text-[#1f2328] md:max-h-[calc(100vh-6rem)] md:overflow-hidden">
      <div className="flex flex-col shrink-0 gap-4 pb-3.5 border-b border-[#eef0f2] md:flex-row md:gap-5 md:pb-[18px]">
        {hasPhotos && (
          <div className="relative shrink-0 w-full h-[240px] overflow-hidden rounded-lg md:w-1/2">
            {/* own swiper id: arrows bind by class, the card behind uses realty id */}
            <CardRealtyPhotos
              realtyId={`details${id}`}
              photos={photos}
              isPwa={true}
            />
          </div>
        )}

        <div className="flex flex-col flex-1 min-w-0 gap-3 md:justify-center">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[19px] font-semibold md:text-[21px]">
                {`Listing ${id}`}
              </span>

              {status && (
                <span
                  className={`${status.color} rounded-[10px] px-[9px] py-0.5 text-xs text-whiteColor`}
                >
                  {status.title}
                </span>
              )}
            </div>

            {address && (
              <div className="flex items-center gap-1.5 text-sm text-[#444]">
                <MapPin
                  size={15}
                  color="#8a91c0"
                  className="shrink-0"
                />
                <span>{address}</span>
              </div>
            )}
          </div>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            {price ? (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[22px] font-semibold md:text-2xl">
                  {`${formatNumberDisplay(price)} €`}
                </span>
                <span className="text-[13px] text-[#6a6f78]">{priceLabel}</span>
              </div>
            ) : (
              <span />
            )}

            {agent?.name && (
              <div className="flex items-center gap-1.5 text-[13px] text-[#6a6f78]">
                <span>Assigned to</span>
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#e3e5f3] text-[10px] font-semibold text-[#5b63a8]">
                  {getInitials(agent.name)}
                </span>
                <span className="text-[#30353c]">{agent.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 pt-4 pb-6 md:flex-1 md:min-h-0 md:gap-5 md:overflow-y-auto md:pt-5 md:pb-7">
        {tiles.length > 0 && (
          <div className="grid grid-cols-3 gap-2 md:grid-cols-5">
            {tiles.map(({ label, value }) => (
              <div
                key={label}
                className="flex flex-col gap-0.5 min-w-0 px-3 py-2.5 rounded-lg bg-[#f6f7f9]"
              >
                <span className="text-xs text-[#6a6f78]">{label}</span>
                <span className="text-base font-semibold truncate md:text-[17px]">
                  {value}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          <div className="flex justify-end pb-1.5">
            <button
              type="button"
              onClick={toggleAll}
              className="px-0.5 py-1 text-[13px] text-[#5b63a8] hover:text-[#3e4590]"
            >
              {isAllOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>

          {sections.map(({ title, rows }) => {
            const isOpen = openSections.has(title);

            return (
              <div
                key={title}
                ref={node => (sectionRefs.current[title] = node)}
                className="flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(title)}
                  className="flex items-center gap-2 w-full h-12 px-3.5 bg-[#f4f4f4] text-left text-[15px] text-[#222] hover:bg-[#ececec] md:h-10 md:text-sm"
                >
                  <span className="flex-1">{title}</span>
                  <span className="text-xs text-[#6a6f78]">{rows.length}</span>
                  <ChevronDown
                    size={16}
                    color="#555"
                    className={cn('transition-transform duration-150', {
                      'rotate-180': isOpen,
                    })}
                  />
                </button>

                {isOpen && (
                  <div className="grid grid-cols-1 gap-x-8 px-3.5 pt-0.5 pb-2.5 md:grid-cols-2 md:px-4 md:pt-1 md:pb-3">
                    {rows.map(({ label, value, isLong }, index) => (
                      <div
                        key={`${label}-${index}`}
                        className={cn(
                          'flex gap-4 min-w-0 py-[11px] border-b border-[#f0f1f3] md:py-[9px]',
                          isLong
                            ? 'flex-col gap-1 md:col-span-2'
                            : 'justify-between'
                        )}
                      >
                        <span className="shrink-0 text-sm text-[#6a6f78] md:text-[13px]">
                          {label}
                        </span>
                        <span
                          className={cn(
                            'min-w-0 text-[15px] whitespace-pre-line md:text-sm',
                            { 'text-right': !isLong }
                          )}
                        >
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Card entry points: owners get the editor, everyone else the read-only view
export const RealtyFormDialog = ({ realty, canEdit, section, onClose }) => (
  <Dialog
    content={
      canEdit ? (
        <AddRealtyForm
          realty={realty}
          isEdit={true}
          accordionToOpen={section}
          handleClose={onClose}
        />
      ) : (
        <RealtyDetailsDialog
          realty={realty}
          section={section}
        />
      )
    }
    classes={'w-full max-w-[768px]'}
    isOpen={true}
    onClose={onClose}
  />
);
